// Optimistic overlay of pending outbox entries on top of React Query cache data.
//
// Pending entries are applied (1) right after a queued write and (2) every time a matching query receives
// fresh data (refetch, hydration from a persisted cache). So invalidating / refetching while other entries are
// still pending never makes the UI "jump back" to the server state.
//
// Query keys handled (must match the hooks that read them):
// - attendance list:   [`/api/v1/schools/${schoolId}/attendance/session/${sessionId}`]  (API.useGetAttendanceForSession)
//                      data: AttendanceResponse[] (generated type says `void`)
// - enrollments:       ['enrollments', schoolId, sessionId, ...]  (useEnrollments(schoolId, sessionId))  data: EnrollmentResponse[]
// - session detail:    ['sessions', schoolId, 'detail', sessionId] (useSession)                              data: SessionResponse
// - session lists:     ['sessions', schoolId, filters]            (useSessions)                              data: SessionResponse[]
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { AttendanceResponse, SessionResponse } from '../generated/sportSchool';
import type { AttendanceOutboxEntry, OutboxEntry, SessionSummaryOutboxEntry } from './types';

const ATTENDANCE_KEY_RE = /\/schools\/([^/]+)\/attendance\/session\/([^/?]+)$/;

export const attendanceForSessionQueryKey = (schoolId: string, sessionId: string) =>
  [`/api/v1/schools/${schoolId}/attendance/session/${sessionId}`] as const;

const isLater = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && Date.parse(a) > Date.parse(b);

type EnrollmentLike = { id: string; sessionId?: string; studentMemberId?: string; attendanceStatus?: string | null };

function overlayAttendanceList(
  data: AttendanceResponse[],
  schoolId: string,
  sessionId: string,
  entries: AttendanceOutboxEntry[]
): AttendanceResponse[] {
  let result = data;
  for (const entry of entries) {
    if (entry.schoolId !== schoolId || entry.sessionId !== sessionId) continue;
    const idx = result.findIndex(
      (a) => a.enrollmentId === entry.enrollmentId || (!!entry.studentMemberId && a.studentMemberId === entry.studentMemberId)
    );
    const existing = idx >= 0 ? result[idx] : undefined;
    // A newer server value wins (the entry will come back as Stale).
    if (existing && isLater(existing.markedAt, entry.markedAt)) continue;
    if (entry.status === null) {
      if (existing) result = result.filter((_, i) => i !== idx);
      continue;
    }
    if (existing && existing.status === entry.status) continue;
    if (!existing && !entry.studentMemberId) continue;
    const next: AttendanceResponse = {
      id: existing?.id ?? `pending:${entry.enrollmentId}`,
      enrollmentId: entry.enrollmentId,
      studentMemberId: existing?.studentMemberId ?? entry.studentMemberId ?? '',
      status: entry.status,
      markedAt: entry.markedAt,
      confirmedAt: existing?.confirmedAt ?? null,
    };
    result = existing ? result.map((a, i) => (i === idx ? next : a)) : [...result, next];
  }
  return result;
}

function overlayEnrollments<T extends EnrollmentLike>(data: T[], entries: AttendanceOutboxEntry[]): T[] {
  let result = data;
  for (const entry of entries) {
    const idx = result.findIndex((e) => e.id === entry.enrollmentId);
    if (idx < 0 || (result[idx].attendanceStatus ?? null) === entry.status) continue;
    result = result.map((e, i) => (i === idx ? { ...e, attendanceStatus: entry.status } : e));
  }
  return result;
}

export function overlaySession<T extends Partial<SessionResponse> & { id: string }>(
  session: T,
  entries: SessionSummaryOutboxEntry[]
): T {
  const entry = entries.find((e) => e.sessionId === session.id);
  if (!entry) return session;
  // A newer server value wins (the server ignores the older client write).
  if (isLater(session.summaryUpdatedAt ?? null, entry.updatedAt)) return session;
  const next: T = { ...session };
  let changed = false;
  const assign = <K extends keyof SessionResponse>(key: K, value: SessionResponse[K]) => {
    if ((next as Partial<SessionResponse>)[key] !== value) {
      (next as Partial<SessionResponse>)[key] = value;
      changed = true;
    }
  };
  if (entry.notes !== undefined) assign('summaryNotes', entry.notes);
  if (entry.topic !== undefined) assign('topic', entry.topic);
  if (entry.complete) {
    if (entry.title?.trim()) assign('title', entry.title.trim());
    if (session.status === 'Scheduled') assign('status', 'Completed');
  }
  if (changed) assign('summaryUpdatedAt', entry.updatedAt);
  return changed ? next : session;
}

/**
 * Returns `data` with pending entries applied (same reference when nothing changed / key not handled).
 * Only `pending` entries are applied — rejected ones show the server state (plus the rejected badge in the UI).
 */
export function overlayQueryData(queryKey: QueryKey, data: unknown, allEntries: OutboxEntry[]): unknown {
  if (data === undefined || data === null) return data;
  const entries = allEntries.filter((e) => e.state === 'pending');
  if (entries.length === 0) return data;
  const head = queryKey[0];

  if (typeof head === 'string' && queryKey.length === 1) {
    const match = ATTENDANCE_KEY_RE.exec(head);
    if (!match || !Array.isArray(data)) return data;
    const attendance = entries.filter((e): e is AttendanceOutboxEntry => e.kind === 'attendance');
    return overlayAttendanceList(data as AttendanceResponse[], match[1], match[2], attendance);
  }

  if (head === 'enrollments' && typeof queryKey[1] === 'string' && typeof queryKey[2] === 'string' && Array.isArray(data)) {
    const [, schoolId, sessionId] = queryKey as [string, string, string];
    const attendance = entries.filter(
      (e): e is AttendanceOutboxEntry => e.kind === 'attendance' && e.schoolId === schoolId && e.sessionId === sessionId
    );
    return attendance.length ? overlayEnrollments(data as EnrollmentLike[], attendance) : data;
  }

  if (head === 'sessions' && typeof queryKey[1] === 'string') {
    const schoolId = queryKey[1];
    const summaries = entries.filter(
      (e): e is SessionSummaryOutboxEntry => e.kind === 'sessionSummary' && e.schoolId === schoolId
    );
    if (summaries.length === 0) return data;
    if (Array.isArray(data)) {
      let changed = false;
      const next = data.map((item) => {
        if (!item || typeof item !== 'object' || typeof (item as { id?: unknown }).id !== 'string') return item;
        const updated = overlaySession(item as SessionResponse, summaries);
        if (updated !== item) changed = true;
        return updated;
      });
      return changed ? next : data;
    }
    if (typeof data === 'object' && typeof (data as { id?: unknown }).id === 'string' && 'startsAt' in (data as object)) {
      return overlaySession(data as SessionResponse, summaries);
    }
  }
  return data;
}

/** Applies pending entries to every cached query they affect. */
export function applyOverlayToCache(queryClient: QueryClient, entries: OutboxEntry[]) {
  if (!entries.some((e) => e.state === 'pending')) return;
  for (const query of queryClient.getQueryCache().getAll()) {
    const data = query.state.data;
    const next = overlayQueryData(query.queryKey, data, entries);
    // Keep `dataUpdatedAt`: overlaying restored (old) data must not make it look fresh and skip the refetch.
    if (next !== data) queryClient.setQueryData(query.queryKey, next, { updatedAt: query.state.dataUpdatedAt });
  }
}

/**
 * Re-applies pending entries whenever a query gets data from the server or from hydration.
 * Manual `setQueryData` calls (including our own) are ignored, so there is no feedback loop.
 */
export function installCacheOverlay(queryClient: QueryClient, getEntries: () => OutboxEntry[]): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    let fromServer: boolean;
    if (event.type === 'added') {
      // Hydration (persisted cache restore) creates queries with data already set — no 'updated' event follows.
      fromServer = event.query.state.data !== undefined;
    } else if (event.type === 'updated') {
      const action = event.action as { type: string; manual?: boolean };
      fromServer = (action.type === 'success' && !action.manual) || action.type === 'setState';
    } else {
      return;
    }
    if (!fromServer) return;
    const entries = getEntries();
    if (!entries.some((e) => e.state === 'pending')) return;
    const query = event.query;
    queueMicrotask(() => {
      const data = query.state.data;
      const next = overlayQueryData(query.queryKey, data, getEntries());
      if (next !== data) queryClient.setQueryData(query.queryKey, next, { updatedAt: query.state.dataUpdatedAt });
    });
  });
}

/** Invalidates everything a sync can change for a school. */
export function invalidateSyncedQueries(queryClient: QueryClient, schoolId: string) {
  queryClient.invalidateQueries({ queryKey: ['enrollments', schoolId] });
  queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === 'string' && query.queryKey[0].includes(`/schools/${schoolId}/attendance`),
  });
  queryClient.invalidateQueries({ queryKey: ['sessions', schoolId] });
}
